import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import GameCard from './GameCard';
import TournamentSummaryCard from './TournamentSummaryCard';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"
import {calculateTournamentRankings} from "../utils/tournamentScoring"
import {filterIgnoredGames} from "../utils/ignoredGames"

/**
 * GameCardList component displays a list of games from a tournament
 * 
 * @param {Object} props - Component props
 * @param {string} props.username - Username to search for
 * @param {string} props.tournamentId - Tournament ID to search in
 */
function GameCardList({ username, tournamentId, sortOrder = 'default', onSortChange }) {
  const tournamentAppConfiguration = useTournamentAppConfiguration()
  const [games, setGames] = useState([]);
  const [overallRank, setOverallRank] = useState('-');
  const [totalPossiblePoints, setTotalPossiblePoints] = useState(0);
  const [bestRankValue, setBestRankValue] = useState(null);
  const [worstRankValue, setWorstRankValue] = useState(null);
  const { theme } = useContext(ThemeContext);
  const { 
    results, 
    loading, 
    refreshing,
    error,
    setIsTournamentPickerOpen
  } = useContext(ResultsContext);

  // Calculate the player's overall tournament ranking using centralized function
  const calculateOverallRank = useCallback((games, username) => {
    if (!username || !games || games.length === 0) {
      setOverallRank('-');
      return;
    }

    const tournamentData = calculateTournamentRankings(games, tournamentAppConfiguration, username, '-');
    setOverallRank(tournamentData.overallRank ? `#${tournamentData.overallRank}` : 'Not ranked');
  }, [tournamentAppConfiguration]);

  // Determine the highest and lowest ranks using centralized function
  const determineHighestAndLowestRanks = useCallback((games, username) => {
    if (!username || !games || games.length === 0) {
      setBestRankValue(null);
      setWorstRankValue(null);
      return;
    }

    const tournamentData = calculateTournamentRankings(games, tournamentAppConfiguration, username, '-');
    
    setBestRankValue(tournamentData.bestRank === Infinity ? null : tournamentData.bestRank);
    setWorstRankValue(tournamentData.worstRank === 0 ? null : tournamentData.worstRank);
  }, [tournamentAppConfiguration]);

  // Process results data when it changes
  useEffect(() => {
    if (results && results.games) {
      const filteredGames = filterIgnoredGames(results.games, tournamentId);
      setGames(filteredGames);

      // Set total possible points (50 per game)
      setTotalPossiblePoints((filteredGames.length || 0) * 50);

      // Calculate overall tournament rank if username is provided
      if (username) {
        calculateOverallRank(filteredGames, username);
        determineHighestAndLowestRanks(filteredGames, username);
      } else {
        setOverallRank('-');
        setBestRankValue(null);
        setWorstRankValue(null);
      }
    }
  }, [results, username, tournamentId, calculateOverallRank, determineHighestAndLowestRanks]);


  // Find user entry in the leaderboard
  const findUserEntry = useCallback((game) => {
    if (!username || !game.leaderboard || game.leaderboard.length === 0) {
      return null;
    }

    return game.leaderboard.find(entry => 
      entry.userName.toLowerCase() === username.toLowerCase()
    );
  }, [username]);

  // Sort games based on the selected sort order
  const sortedGames = useMemo(() => {
    const sorted = [...games];
    if (sortOrder === 'alpha') {
      return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortOrder === 'rank') {
      return sorted.sort((a, b) => {
        const userA = findUserEntry(a);
        const userB = findUserEntry(b);
        const rankA = userA ? userA.rank : Infinity;
        const rankB = userB ? userB.rank : Infinity;
        
        if (rankA !== rankB) {
          return rankA - rankB;
        }
        // If ranks are equal (e.g. both Infinity), fall back to alphabetical
        return (a.title || '').localeCompare(b.title || '');
      });
    }
    return sorted; // 'default'
  }, [games, sortOrder, findUserEntry]);


  if (loading) {
    return (
      <div className={`loading ${theme}`}>Loading games data...</div>
    );
  }

  if (error) {
    return (
      <div className={`error ${theme}`}>{error}</div>
    );
  }

  if (!tournamentId) {
    return (
      <div 
        className={`info ${theme} clickable-info`} 
        onClick={() => setIsTournamentPickerOpen(true)}
      >
        Please select a tournament ID to view games.
      </div>
    );
  }

  if (games.length === 0 && !loading && !refreshing) {
    return (
      <div className={`info ${theme}`}>No games found for this tournament.</div>
    );
  }

  return (
    <div className="games-container">
      {/* Combined Tournament Summary and Games */}
      <div className="results">
        {/* Tournament Summary Card as the first card */}
        <TournamentSummaryCard 
          games={games} 
          username={username} 
          totalPossiblePoints={totalPossiblePoints}
          overallRank={overallRank}
        />
        {/* Tournament Game Cards */}
        {sortedGames.map(game => {
          const userEntry = findUserEntry(game);
          const isHighestRank = userEntry && bestRankValue !== null && userEntry.rank === bestRankValue;
          const isLowestRank = userEntry && worstRankValue !== null && userEntry.rank === worstRankValue;

          return (
            <GameCard
              key={game.id || game.title}
              image={game.image}
              title={game.title}
              rank={userEntry?.rank}
              score={userEntry?.score}
              initials={userEntry?.initials}
              points={userEntry?.points}
              isRanked={!!userEntry}
              username={username}
              leaderboard={game.leaderboard || []}
              isHighestRank={isHighestRank}
              isLowestRank={isLowestRank}
            />
          );
        })}


      </div>
    </div>
  );
}

export default GameCardList;
